/**
 * Générateur — Définition du symétrique d'un point par rapport à une droite
 * Attendu ID : 672 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - definition_symetrique_point_qcm       : QCM 4 choix (définition canonique)
 *   - caracterisation_symetrie_vf           : V/F 3V + 3F (points fixes, involutivité)
 *   - distance_axe_saisie                   : saisie numérique (conservation distance)
 *   - etapes_construction_point_ordonner    : 5 étapes à ordonner (procédure)
 *   - involutivite_cas_particuliers_qcm     : QCM 4 choix (points fixes, involutivité)
 *
 * Spécificité : 100 % texte pur (pas de SVG). Dans ce cluster 37, l'axe est
 * noté `(d)` ou `(Δ)`. Les lettres des points sont tirées dans `LETTRES`
 * (pool 23 lettres, I exclu). Pas de restriction sur O dans ce cluster.
 *
 * Partition avec attendus voisins :
 *   - 660 : médiatrice d'un segment (outil définitionnel).
 *   - 672 : définition du symétrique d'un POINT (ce générateur).
 *   - 673 : propriétés et construction des symétriques de FIGURES.
 *
 * Variantes estimées : 506 couples de lettres × 19 distances × 2-3 variantes.
 * Le seul format avec <500 fingerprints (Format 4) est renforcé par 2 axes ×
 * 2 instruments = 92 fingerprints.
 */

import {
    randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeOrdonner,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 672;
// Pool : 23 lettres (I exclu pour confusion I↔1).
const LETTRES = 'ABCDEFGHJKLMNOPQRSTUVWYZ'.split('').filter(c => c !== 'I');

const L_DEF    = 'Définition du symétrique d\'un point (QCM)';
const L_VF     = 'Caractérisation de la symétrie axiale (V/F)';
const L_DIST   = 'Distance à l\'axe (saisie)';
const L_ETAPES = 'Étapes de construction du symétrique d\'un point (ordering)';
const L_INVOL  = 'Involutivité et cas particuliers (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_DEF,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

const fr = (x) => {
    if (Number.isInteger(x)) return String(x);
    return String(x).replace('.', ',');
};

// ─── Format 1 — Définition du symétrique (QCM) ───────────────

const genDefinitionSymetrique = () => {
    const [L1, L2] = pickLetters(2);
    const ax = randomChoice(AXES_POOL);
    let choix, bonne, texte;

    if (Math.random() < 0.5) {
        // V1 — définition directe (le point L2 est mentionné comme point extérieur)
        bonne = `Le point ${L1}' tel que ${ax} est la médiatrice de [${L1}${L1}'].`;
        choix = [
            bonne,
            `Le point ${L1}' tel que (${L1}${L1}') est parallèle à ${ax}.`,
            `Le point ${L1}' tel que ${ax} passe par le milieu de [${L1}${L1}'].`,
            `Le point ${L1}' tel que ${ax} est perpendiculaire à (${L1}${L1}').`,
        ];
        texte = `<p>Soit un point ${L1} et une droite ${ax} ne passant pas par ${L1}. Quel est le symétrique ${L1}' de ${L1} par rapport à ${ax} ?</p>`;
    } else {
        // V2 — définition inversée
        bonne = `${ax} est la médiatrice de [${L1}${L2}].`;
        choix = [
            bonne,
            `${ax} passe par ${L1} et ${L2}.`,
            `${ax} est perpendiculaire à [${L1}${L2}] mais ne passe pas forcément par son milieu.`,
            `${ax} passe par le milieu de [${L1}${L2}] mais n'est pas forcément perpendiculaire.`,
        ];
        texte = `<p>On dit que ${L2} est le symétrique de ${L1} par rapport à la droite ${ax}. Que peut-on dire de ${ax} ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_DEF));
    payload._meta.generatorKey = 'definition_symetrique_point_qcm';
    return payload;
};

// ─── Format 2 — Caractérisation (V/F) ────────────────────────

const buildAffirmationsVF = () => {
    const [L1, L2] = pickLetters(2);
    return {
        VRAIS: {
            V1: () => ({
                id: 'V1', verite: 0,
                texte: 'Si un point appartient à la droite (d), alors son symétrique par rapport à (d) est lui-même.',
            }),
            V2: () => ({
                id: 'V2', verite: 0,
                texte: `Si ${L2} est le symétrique de ${L1} par rapport à (d), alors ${L1} est le symétrique de ${L2} par rapport à (d).`,
            }),
            V3: () => ({
                id: 'V3', verite: 0,
                texte: `Si ${L2} est le symétrique de ${L1} par rapport à (d), alors ${L1} et ${L2} sont à la même distance de (d).`,
            }),
            V4: () => ({
                id: 'V4', verite: 0,
                texte: 'Le symétrique d\'un point du plan par rapport à une droite est un point.',
            }),
            V5: () => ({
                id: 'V5', verite: 0,
                texte: `Si ${L2} est le symétrique de ${L1} par rapport à (d) et que ${L1} ≠ ${L2}, alors la droite (${L1}${L2}) est perpendiculaire à (d).`,
            }),
        },
        FAUX: {
            F1: () => ({
                id: 'F1', verite: 1,
                texte: 'Le symétrique d\'un point par rapport à une droite est toujours différent de ce point.',
            }),
            F2: () => ({
                id: 'F2', verite: 1,
                texte: `Si ${L2} est le symétrique de ${L1} par rapport à (d), alors (${L1}${L2}) est parallèle à (d).`,
            }),
            F3: () => ({
                id: 'F3', verite: 1,
                texte: 'La symétrie axiale envoie tous les points sur un même point de l\'axe.',
            }),
            F4: () => ({
                id: 'F4', verite: 1,
                texte: `Si ${L2} est le symétrique de ${L1} par rapport à (d), alors le symétrique de ${L2} par rapport à (d) est un troisième point différent de ${L1}.`,
            }),
            F5: () => ({
                id: 'F5', verite: 1,
                texte: 'Pour que (d) soit l\'axe de symétrie entre deux points, il suffit qu\'elle soit perpendiculaire à la droite joignant les deux points.',
            }),
        },
    };
};

const genCaracterisationVF = () => {
    let tries = 0;
    while (tries < 5) {
        const builders = buildAffirmationsVF();
        const idsV = randomChoiceMultiple(Object.keys(builders.VRAIS), 3);
        const idsF = randomChoiceMultiple(Object.keys(builders.FAUX), 3);
        const vrais = idsV.map(id => builders.VRAIS[id]());
        const faux  = idsF.map(id => builders.FAUX[id]());
        const mixed = shuffle([...vrais, ...faux]);

        const lignes = mixed.map(a => a.texte);
        const bonnesReponses = mixed.map(a => a.verite);

        if (new Set(lignes).size === lignes.length) {
            const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'caracterisation_symetrie_vf';
            return payload;
        }
        tries++;
    }

    // Fallback déterministe
    const builders = buildAffirmationsVF();
    const vrais = ['V1', 'V2', 'V3'].map(id => builders.VRAIS[id]());
    const faux  = ['F1', 'F3', 'F5'].map(id => builders.FAUX[id]());
    const mixed = shuffle([...vrais, ...faux]);
    const lignes = mixed.map(a => a.texte);
    const bonnesReponses = mixed.map(a => a.verite);
    const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'caracterisation_symetrie_vf';
    return payload;
};

// ─── Format 3 — Distance à l'axe (saisie) ────────────────────

const DISTANCES_POOL = [
    1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5,
    6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
];

const genDistanceSaisie = () => {
    const [L1, L2] = pickLetters(2);
    const r = randomChoice(DISTANCES_POOL);

    let texte;
    if (Math.random() < 0.5) {
        // V1 — distance de M' sachant celle de M
        texte = `<p>${L2} est le symétrique de ${L1} par rapport à (d). On sait que ${L1} est à ${fr(r)} cm de (d). À quelle distance de (d) se trouve ${L2} (en cm) ?</p>`;
    } else {
        // V2 — distance de M sachant celle de M'
        texte = `<p>Dans une symétrie d'axe (d), le symétrique de ${L1} est ${L2}. Si la distance de ${L2} à (d) est ${fr(r)} cm, quelle est la distance de ${L1} à (d) (en cm) ?</p>`;
    }

    const payload = makeSaisie(fr(r), texte, {
        ...opts(L_DIST),
        suffixe: ' cm',
        strict: false,
    });
    payload._meta.generatorKey = 'distance_axe_saisie';
    return payload;
};

// ─── Format 4 — Étapes de construction (ordering) ────────────

// Notations standard d'un axe de symétrie : elles ne supposent aucun point
// nommé sur la droite, contrairement à un axe noté (BC). Élargir ce pool est le
// seul levier de variété qui ne touche à aucune convention — la notation A / A'
// reste intacte, le prime signalant que le point est l'image.
// Sert les formats 1, 4 et 6.
const AXES_POOL = ['(d)', '(Δ)', '(d₁)', '(Δ₁)', '(d₂)', '(Δ₂)', '(D)', '(D₁)'];

// Pools de formulations pour chaque étape → largement > 100 fingerprints.
// Item 1 : 3 variantes de formulation (instruments).
const ITEM1_POOL = [
    (L, ax) => `Tracer la droite passant par ${L} et perpendiculaire à ${ax}, à l'aide de l'équerre.`,
    (L, ax) => `Tracer la droite passant par ${L} et perpendiculaire à ${ax}, à l'aide de l'équerre et de la règle.`,
    (L, ax) => `À l'équerre, tracer la perpendiculaire à ${ax} passant par ${L}.`,
];

// Item 3 : 2 variantes.
const ITEM3_POOL = [
    (L) => `Mesurer la longueur ${L}H à la règle graduée.`,
    (L) => `Mesurer la distance ${L}H avec la règle graduée.`,
];

// Item 4 : 2 variantes.
const ITEM4_POOL = [
    (L, ax) => `Reporter la longueur ${L}H de l'autre côté de ${ax} sur la même perpendiculaire, à partir de H.`,
    (L, ax) => `De l'autre côté de ${ax}, reporter la même longueur ${L}H sur la perpendiculaire, à partir de H.`,
];

// Item 2 : 2 variantes.
const ITEM2_POOL = [
    (ax) => `Noter H le point d'intersection de cette perpendiculaire avec ${ax}.`,
    (ax) => `Appeler H le point où cette perpendiculaire coupe ${ax}.`,
];

// Item 5 : 2 variantes.
const ITEM5_POOL = [
    (L, ax) => `Nommer ${L}' le point ainsi obtenu : c'est le symétrique de ${L} par rapport à ${ax}.`,
    (L, ax) => `Le point ${L}' ainsi obtenu est le symétrique de ${L} par rapport à ${ax}.`,
];

const genEtapesConstruction = () => {
    // H est écrit en dur dans les étapes comme nom du pied de la perpendiculaire.
    // On le retire donc du pool, sinon le point à construire et son projeté sur
    // l'axe portent le même nom et l'énoncé se contredit (« mesurer HH »).
    const [L1, L2] = pickLettresSains(LETTRES.filter(c => c !== 'H'), 2);
    const ax = randomChoice(AXES_POOL);
    const item1 = randomChoice(ITEM1_POOL);
    const item2 = randomChoice(ITEM2_POOL);
    const item3 = randomChoice(ITEM3_POOL);
    const item4 = randomChoice(ITEM4_POOL);
    const item5 = randomChoice(ITEM5_POOL);

    const items = [
        item1(L1, ax),
        item2(ax),
        item3(L1),
        item4(L1, ax),
        item5(L1, ax),
    ];

    // On mentionne L2 dans l'énoncé pour démultiplier les fingerprints (contexte
    // neutre : L2 est un point du plan non utilisé dans la construction mais nommé).
    const texte = `<p>Dans un plan contenant un point ${L2}, voici les étapes pour construire le symétrique ${L1}' du point ${L1} par rapport à la droite ${ax}. Remets-les dans le bon ordre.</p>`;

    const payload = makeOrdonner(items, texte, opts(L_ETAPES));
    payload._meta.generatorKey = 'etapes_construction_point_ordonner';
    return payload;
};

// ─── Format 6 — Involutivité & cas particuliers (QCM) ────────

const genInvolutiviteQcm = () => {
    const [L1, L2, L3] = pickLetters(3);
    const ax = randomChoice(AXES_POOL);
    const r = Math.random();
    let choix, bonne, texte;

    if (r < 1 / 3) {
        // V1 — point sur l'axe (paramétré par L1 + L2 comme point de référence)
        bonne = `${L1} lui-même.`;
        choix = [
            bonne,
            `Un point de ${ax} différent de ${L1}.`,
            `Un point qui n'appartient pas à ${ax}.`,
            `${L2} lui-même.`,
        ];
        texte = `<p>On considère un point ${L1} appartenant à la droite ${ax} et un point ${L2} quelconque du plan. Quel est le symétrique de ${L1} par rapport à ${ax} ?</p>`;
    } else if (r < 2 / 3) {
        // V2 — involutivité
        bonne = `${L1}`;
        choix = [
            bonne,
            `${L2} lui-même`,
            `Un troisième point, distinct de ${L1} et de ${L2}`,
            'On ne peut pas le savoir.',
        ];
        texte = `<p>${L2} est le symétrique de ${L1} par rapport à la droite ${ax}. Quel est le symétrique de ${L2} par rapport à cette même droite ${ax} ?</p>`;
    } else {
        // V3 — nombre de points fixes (paramétré par L1, L2, L3 pour fingerprint unique)
        bonne = `Tous les points de ${ax}.`;
        choix = [
            bonne,
            'Aucun.',
            'Un seul.',
            'Deux.',
        ];
        texte = `<p>On considère trois points ${L1}, ${L2} et ${L3} appartenant à une droite ${ax}. Combien de points de ${ax} sont leurs propres symétriques par rapport à ${ax} ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_INVOL));
    payload._meta.generatorKey = 'involutivite_cas_particuliers_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    definition_symetrique_point_qcm:         genDefinitionSymetrique,
    caracterisation_symetrie_vf:             genCaracterisationVF,
    distance_axe_saisie:                     genDistanceSaisie,
    etapes_construction_point_ordonner:      genEtapesConstruction,
    involutivite_cas_particuliers_qcm:       genInvolutiviteQcm,
};

export const CATEGORIES = [
    {
        id: 'definition_symetrique_point',
        label: 'Définition du symétrique d\'un point',
        formatsList: [
            { key: 'definition_symetrique_point_qcm', label: 'QCM' },
        ],
    },
    {
        id: 'proprietes_symetrique_point',
        label: 'Propriétés du symétrique (points fixes, involutivité)',
        formatsList: [
            { key: 'caracterisation_symetrie_vf',       label: 'V/F' },
            { key: 'involutivite_cas_particuliers_qcm', label: 'QCM' },
        ],
    },
    {
        id: 'construction_symetrique_point',
        label: 'Construire le symétrique d\'un point',
        formatsList: [
            { key: 'etapes_construction_point_ordonner', label: 'Ordonner' },
        ],
    },
    {
        id: 'application_symetrique_point',
        label: 'Appliquer (conservation des distances à l\'axe)',
        formatsList: [
            { key: 'distance_axe_saisie', label: 'Saisie' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    LETTRES,
    DISTANCES_POOL,
    pickLetters,
    buildAffirmationsVF,
    genDefinitionSymetrique,
    genCaracterisationVF,
    genDistanceSaisie,
    genEtapesConstruction,
    genInvolutiviteQcm,
};
